/**
 * V2 sync (Theme 10, from 7cc48382).
 *
 * `useTelemetryNotice` — show a one-time toast on first session where the
 * logged-in user is identified as a tester or super-admin. Implicit consent
 * model: by being a tester they've already agreed; the toast is courtesy,
 * not a permission prompt.
 *
 * Persists "shown" state per user-id in localStorage so the toast doesn't
 * nag every login on the same machine. Cleared on logout would be nice but
 * not necessary — different user_id key means different toast cycle.
 */
import { useEffect } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/lib/auth'

const NOTICE_STORAGE_PREFIX = 'gdb.telemetry.noticeShown.'

export function useTelemetryNotice(): void {
  const { authStatus, isLoggedIn } = useAuth()

  useEffect(() => {
    if (!isLoggedIn) return
    const user = authStatus?.user
    if (!user?.telemetryEnabled) return

    const storageKey = `${NOTICE_STORAGE_PREFIX}${user.id}`
    if (localStorage.getItem(storageKey) === '1') return

    // Identify the cohort so the message reads correctly. Both can apply —
    // priority: super-admin (broader privilege) → tester.
    const cohort = user.isSuperAdmin
      ? 'admin'
      : user.isTester
        ? 'tester'
        : 'tester' // shouldn't happen given telemetryEnabled gate above, but safe.

    toast.info('Telemetry enabled', {
      description: `Errors and warnings from this session will be sent to GameDevBuddy because you're a ${cohort}. This helps us spot regressions early.`,
      duration: 8000,
    })

    localStorage.setItem(storageKey, '1')
  }, [authStatus, isLoggedIn])
}
